import * as React from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * A coloured icon tile: the CategoryChip shape, opened up to the rest of the product.
 * A 34-40px rounded square holding a 17/19px lucide glyph, stroke 2, never filled.
 * (Design system, section 05 — "Category chips" and "Colour follows the ground".)
 *
 * `solid` = the strong colour as the ground, glyph on top of it.
 * `soft`  = the tinted ground, glyph in the strong colour. The default, because most
 * places want a hint of colour rather than a block of it.
 */

export type IconTileTone =
  | "direct"
  | "couples"
  | "individual"
  | "group"
  | "indirect"
  | "other"
  | "primary"
  | "warning"
  | "success"
  | "info"
  | "destructive";

/** Written out so Tailwind sees every class. A template string would produce no CSS. */
const SOLID: Record<IconTileTone, string> = {
  direct: "bg-cat-direct text-primary-foreground",
  couples: "bg-cat-couples text-foreground",
  individual: "bg-cat-individual text-primary-foreground",
  group: "bg-cat-group text-primary-foreground",
  indirect: "bg-cat-indirect text-primary-foreground",
  other: "bg-cat-other text-primary-foreground",
  primary: "bg-primary text-primary-foreground",
  warning: "bg-warning text-warning-foreground",
  success: "bg-success text-success-foreground",
  info: "bg-info text-info-foreground",
  destructive: "bg-destructive text-destructive-foreground",
};

const SOFT: Record<IconTileTone, string> = {
  direct: "bg-cat-direct-soft text-cat-direct",
  couples: "bg-cat-couples-soft text-cat-couples",
  individual: "bg-cat-individual-soft text-cat-individual",
  group: "bg-cat-group-soft text-cat-group",
  indirect: "bg-cat-indirect-soft text-cat-indirect",
  other: "bg-cat-other-soft text-cat-other",
  primary: "bg-muted text-primary",
  warning: "bg-warning text-warning-foreground",
  success: "bg-success text-success-foreground",
  info: "bg-info text-info-foreground",
  destructive: "bg-destructive/10 text-destructive",
};

export interface IconTileProps {
  icon: LucideIcon;
  tone: IconTileTone;
  /** soft (default) = tinted ground. solid = the strong colour as the ground. */
  variant?: "solid" | "soft";
  /** sm = 34px (rows, nav), md = 40px (headers). */
  size?: "sm" | "md";
  /**
   * What the icon means. Read to screen readers unless the text beside the tile
   * already says it — then pass `labelled` and the tile goes silent.
   */
  label: string;
  /** The adjacent text already carries the name: hide this tile from the reader. */
  labelled?: boolean;
  className?: string;
}

export function IconTile({
  icon: Icon,
  tone,
  variant = "soft",
  size = "sm",
  label,
  labelled = false,
  className,
}: IconTileProps) {
  return (
    <span
      data-slot="icon-tile"
      data-tone={tone}
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-[12px]",
        size === "sm" ? "size-[34px]" : "size-[40px]",
        variant === "solid" ? SOLID[tone] : SOFT[tone],
        className,
      )}
    >
      <Icon size={size === "sm" ? 17 : 19} strokeWidth={2} aria-hidden={true} />
      {labelled ? null : <span className="sr-only">{label}</span>}
    </span>
  );
}
